import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import {
  ClipboardCheck,
  Clock,
  CheckCircle2,
  X,
  Sparkles,
  ArrowRight,
  BrainCircuit,
  Heart,
  Trophy,
  TrendingUp,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { AssessmentConfig, AssessmentHistoryEntry } from './types'
import { GroupId } from '../../config/systemConfig'
import { BreathingExercise } from './BreathingExercise'
import {
  getAssessmentHistory,
  checkAssessmentCooldown,
} from './utils/assessmentStorage'
import { ASSESSMENT_COOLDOWN } from '../../config/constants'
import { formatRemainingTime } from './utils/timeUtils'
interface AssessmentStartPageProps {
  selectedGroup: GroupId
  onStart: (config: AssessmentConfig) => void
  onCancel: () => void
}
export function AssessmentStartPage({
  selectedGroup,
  onStart,
  onCancel,
}: AssessmentStartPageProps) {
  const [showBreathing, setShowBreathing] = useState(false)
  // Load assessment history for this group
  const history = useMemo(() => {
    try {
      return getAssessmentHistory(selectedGroup).slice(0, 3)
    } catch {
      return []
    }
  }, [selectedGroup])
  // Check per-group cooldown (CONFLICT-14 resolution)
  const cooldown = useMemo(() => {
    return checkAssessmentCooldown(selectedGroup)
  }, [selectedGroup])
  const handleStart = () => {
    // Fixed configuration: 30 questions, all modules, no mastery tracking
    const config: AssessmentConfig = {
      questionCount: 30,
      moduleFilter: null,
      minModules: 3,
      countsForMastery: false,
      seed: Date.now(),
    }
    onStart(config)
  }
  // Breathing exercise view
  if (showBreathing) {
    return (
      <div className="flex-1 bg-indigo-50 flex items-center justify-center p-4">
        <motion.div
          initial={{
            opacity: 0,
            scale: 0.9,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          className="w-full max-w-md"
        >
          <BreathingExercise
            onComplete={() => setShowBreathing(false)}
            onSkip={() => setShowBreathing(false)}
          />
        </motion.div>
      </div>
    )
  }
  return (
    <div className="flex-1 bg-slate-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
          ease: 'easeOut',
        }}
        className="w-full max-w-2xl my-auto"
      >
        <Card className="overflow-hidden border-2 border-indigo-100 shadow-2xl shadow-indigo-100/50 rounded-[2rem]">
          {/* Header - Warm & Friendly */}
          <div className="bg-gradient-to-br from-indigo-600 to-violet-600 p-8 text-white relative overflow-hidden">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-16 -mt-16 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/30 rounded-full -ml-10 -mb-10 blur-2xl" />

            <button
              onClick={onCancel}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm z-10"
              aria-label="Sluiten"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            <div className="relative z-10">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md shadow-inner border border-white/30 transform rotate-3">
                  <span className="text-4xl">📝</span>
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-wider mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    Oefentoets Groep {selectedGroup}
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2 text-white drop-shadow-sm">
                    Klaar voor de start?
                  </h1>
                  <p className="text-indigo-100 text-lg font-medium leading-relaxed max-w-lg">
                    Laat zien wat je al kunt! Geen stress, het is maar om te
                    oefenen.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8 bg-white">
            {/* How it works - Simplified */}
            <div>
              <h2 className="text-xl font-black text-slate-900 mb-4 flex items-center gap-2">
                <TargetIcon className="w-6 h-6 text-indigo-600" />
                Zo werkt het
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mb-3 text-indigo-600">
                    <ClipboardCheck className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">30 Vragen</h3>
                  <p className="text-sm text-slate-600 font-medium">
                    Over rekenen, taal en meer.
                  </p>
                </div>

                <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mb-3 text-indigo-600">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">~15 Minuten</h3>
                  <p className="text-sm text-slate-600 font-medium">
                    Neem rustig de tijd.
                  </p>
                </div>

                <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mb-3 text-indigo-600">
                    <BrainCircuit className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">Jouw Niveau</h3>
                  <p className="text-sm text-slate-600 font-medium">
                    Zie direct waar je staat.
                  </p>
                </div>
              </div>
            </div>

            {/* Friendly Tip Section */}
            <div className="bg-amber-50 border-2 border-amber-100 rounded-2xl p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100/50 rounded-full -mr-10 -mt-10 blur-2xl" />

              <div className="flex items-start gap-4 relative z-10">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-amber-100">
                  💡
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg mb-1">
                    Belangrijke tip
                  </h3>
                  <p className="text-amber-800 font-medium leading-relaxed">
                    Dit is een <strong>oefentoets</strong>. Het telt niet mee
                    voor je rapport, maar helpt je wel om te zien waar je nog
                    mee kunt oefenen. Fouten maken mag!
                  </p>
                </div>
              </div>
            </div>

            {/* Previous Assessment History */}
            {history.length > 0 ? (
              <div>
                <h2 className="text-lg font-black text-slate-900 mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Vorige toetsen
                </h2>
                <div className="space-y-2">
                  {history.map((entry, i) => {
                    const date = new Date(entry.completedAt)
                    const dateStr = date.toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short',
                    })
                    const scoreColor =
                      entry.score >= 70
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                        : entry.score >= 50
                          ? 'text-amber-700 bg-amber-50 border-amber-200'
                          : 'text-rose-700 bg-rose-50 border-rose-200'
                    return (
                      <motion.div
                        key={entry.attemptId}
                        initial={{
                          opacity: 0,
                          x: -10,
                        }}
                        animate={{
                          opacity: 1,
                          x: 0,
                        }}
                        transition={{
                          delay: i * 0.1,
                        }}
                        className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-slate-500 font-medium w-16">
                            {dateStr}
                          </div>
                          <div className="text-sm text-slate-600 font-medium">
                            {entry.totalQuestions} vragen
                          </div>
                        </div>
                        <div
                          className={`text-sm font-black px-3 py-1 rounded-full border ${scoreColor}`}
                        >
                          {entry.score}%
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-2 font-medium flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Laat zien dat je nog beter kunt!
                </p>
              </div>
            ) : (
              <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-5 text-center">
                <div className="text-3xl mb-2">🌟</div>
                <p className="text-indigo-900 font-bold text-lg">
                  Dit is je eerste toets!
                </p>
                <p className="text-indigo-600 text-sm font-medium mt-1">
                  Na afloop zie je precies waar je goed in bent.
                </p>
              </div>
            )}

            {/* Breathing Exercise CTA */}
            <div className="flex items-center justify-between bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm text-indigo-500">
                  <Heart className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <div className="font-bold text-slate-700">Spannend?</div>
                  <div className="text-sm text-slate-500 font-medium">
                    Doe eerst even een ademhalingsoefening
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setShowBreathing(true)}
                variant="secondary"
                size="sm"
                className="bg-white shadow-sm border-slate-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200"
              >
                Even rustig worden
              </Button>
            </div>

            {/* Cooldown Warning (per-group) */}
            {!cooldown.canStart && (
              <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-5 relative overflow-hidden">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-slate-200">
                    ⏳
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-700 text-lg mb-1">
                      Even wachten
                    </h3>
                    <p className="text-slate-600 font-medium leading-relaxed">
                      Je hebt recent al een toets gemaakt voor groep{' '}
                      {selectedGroup}. Wacht nog{' '}
                      <strong>
                        {formatRemainingTime(cooldown.remainingMs)}
                      </strong>{' '}
                      voordat je opnieuw kunt beginnen.
                    </p>
                    <p className="text-slate-400 text-sm mt-2 font-medium">
                      Gebruik deze tijd om te oefenen met de vakken waar je nog
                      mee kunt groeien!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Actions */}
            <div className="pt-2 space-y-3">
              <Button
                onClick={handleStart}
                variant="primary"
                size="xl"
                fullWidth
                disabled={!cooldown.canStart}
                className={`h-16 text-xl rounded-2xl shadow-xl transition-all ${
                  cooldown.canStart
                    ? 'shadow-indigo-200 hover:shadow-2xl hover:-translate-y-1'
                    : 'opacity-60 cursor-not-allowed shadow-none'
                }`}
              >
                <span className="mr-2">
                  {cooldown.canStart
                    ? 'Ik ben er klaar voor!'
                    : 'Even geduld...'}
                </span>
                <span className="text-2xl">
                  {cooldown.canStart ? '💪' : '⏳'}
                </span>
              </Button>

              <Button
                onClick={onCancel}
                variant="ghost"
                size="lg"
                fullWidth
                className="text-slate-500 hover:text-slate-800"
              >
                {cooldown.canStart
                  ? 'Toch nog even wachten'
                  : 'Terug naar overzicht'}
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  )
}
// Helper component for the target icon
function TargetIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  )
}
/** Format remaining cooldown time in Dutch */
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000))
  if (minutes <= 1) return 'minder dan een minuut'
  if (minutes < 60) return `${minutes} minuten`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (remainingMinutes === 0) return `${hours} uur`
  return `${hours} uur en ${remainingMinutes} minuten`
}
